import React, { useState } from 'react';
import { Box, Chip, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useDataContext } from '../Context/useDataContext';
import { useMessageContext } from '../Context/useMessageContext';
import { PushMessageIcon } from './PushMessageModal';

type InfoTab = 'general' | 'firsttee';

interface InfoDrawerProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const InfoDrawer = ({ open, setOpen }: InfoDrawerProps) => {
    const { eventData } = useDataContext();
    const { activeMessages, openReview } = useMessageContext();

    const [activeTab, setActiveTab] = useState<InfoTab>('general');

    const eventGeneralInfo: string = eventData?.eventGeneralInfo ?? '';
    const eventFirstTeeInfo: string = eventData?.eventFirstTeeInfo ?? '';
    const legacyInfoItems: { name: string; value: string }[] = eventData?.eventInfo ?? [];

    const hasFirstTee = eventFirstTeeInfo.trim().length > 0;

    const tabs: { id: InfoTab; label: string }[] = [
        { id: 'general', label: 'General' },
        ...(hasFirstTee ? [{ id: 'firsttee' as InfoTab, label: 'First Tee' }] : []),
    ];

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={() => setOpen(false)}
            PaperProps={{
                sx: {
                    height: '80dvh',
                    borderRadius: '16px 16px 0 0',
                    backgroundColor: 'sepia.background',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Drag handle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'sepia.light',
                    opacity: 0.7,
                }} />
            </Box>

            {/* Title row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between"
                sx={{ px: 3, pt: 1, pb: 1, flexShrink: 0 }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, textAlign: 'left', color: 'sepia.dark' }}
                >
                    Event Information
                </Typography>
                {activeMessages.length > 0 && (
                    <Stack direction="row" spacing={0.5}>
                        {activeMessages.map((msg) => (
                            <IconButton
                                key={msg.pushedAt}
                                onClick={() => openReview(msg.pushedAt)}
                                sx={{
                                    color: 'rgb(184, 115, 51)',
                                    filter: 'drop-shadow(0 0 4px rgba(184, 115, 51, 0.7))',
                                    padding: '4px',
                                }}
                            >
                                <PushMessageIcon icon={msg.icon} />
                            </IconButton>
                        ))}
                    </Stack>
                )}
            </Stack>

            {/* Chip tabs — only shown when there is a First Tee section */}
            {hasFirstTee && (
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    sx={{ px: 3, pb: 1.5, flexShrink: 0 }}
                >
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <Chip
                                key={tab.id}
                                label={tab.label}
                                onClick={() => setActiveTab(tab.id)}
                                sx={{
                                    fontWeight: isActive ? 700 : 400,
                                    backgroundColor: isActive ? 'primary.light' : 'white',
                                    color: isActive ? 'white' : 'text.primary',
                                    border: '1px solid',
                                    borderColor: isActive ? 'primary.light' : 'divider',
                                }}
                            />
                        );
                    })}
                </Stack>
            )}

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
                {activeTab === 'general' && (
                    <>
                        {eventGeneralInfo.trim().length > 0 ? (
                            <Box
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: (theme) => theme.typography.fontFamily,
                                    fontSize: (theme) => theme.typography.fontSize,
                                    '& p': { margin: '0 0 0.5em 0' },
                                    '& ul, & ol': { paddingLeft: '1.5em', margin: '0 0 0.5em 0' },
                                    '& a': { color: 'primary.main' },
                                }}
                                dangerouslySetInnerHTML={{ __html: eventGeneralInfo }}
                            />
                        ) : legacyInfoItems.length > 0 ? (
                            legacyInfoItems.map((item, index) => (
                                <Box key={index}>
                                    {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, textAlign: 'left', mb: 0.5, color: 'sepia.dark' }}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ textAlign: 'left', color: 'text.secondary' }}
                                    >
                                        {item.value || '—'}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" sx={{ color: 'sepia.light' }}>
                                No information available.
                            </Typography>
                        )}
                    </>
                )}

                {activeTab === 'firsttee' && (
                    <Box
                        sx={{
                            color: 'text.primary',
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: (theme) => theme.typography.fontSize,
                            '& p': { margin: '0 0 0.5em 0' },
                            '& ul, & ol': { paddingLeft: '1.5em', margin: '0 0 0.5em 0' },
                            '& a': { color: 'primary.main' },
                        }}
                        dangerouslySetInnerHTML={{ __html: eventFirstTeeInfo }}
                    />
                )}
            </Box>
        </Drawer>
    );
};
